import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { orderService } from '../services/orderService';
import { addressService } from '../services/addressService';
import { shippingService } from '../services/shippingService';
import ShippingForm from '../components/checkout/ShippingForm';
import ShippingMethod from '../components/checkout/ShippingMethod';
import PaymentMethod from '../components/checkout/PaymentMethod';
import OrderSummary from '../components/checkout/OrderSummary';
import Loading from '../components/common/Loading';
import { SHIPPING_METHODS, PAYMENT_METHODS } from '../utils/constants';
import './Checkout.css';

const Checkout = () => {
  const navigate = useNavigate();
  const { cart, cartItemCount } = useCart();
  const { isAuthenticated, user } = useAuth();
  const [step, setStep] = useState(1);
  const [shippingInfo, setShippingInfo] = useState(null);
  const [shippingMethod, setShippingMethod] = useState(SHIPPING_METHODS.STANDARD);
  const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHODS.CASH_ON_DELIVERY);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [defaultAddressLoaded, setDefaultAddressLoaded] = useState(false);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const subtotal = useMemo(
    () =>
      cart?.items?.reduce((sum, item) => {
        const price = parseFloat(item.product?.price || 0);
        return sum + price * item.quantity;
      }, 0) || 0,
    [cart]
  );

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!cart || cartItemCount === 0) {
      navigate('/cart');
    }
  }, [isAuthenticated, cart, cartItemCount, navigate]);

  // Load saved addresses on mount; fall back to user profile if no saved address.
  useEffect(() => {
    if (!isAuthenticated) return;
    addressService
      .list()
      .then((addrs) => {
        setSavedAddresses(addrs);
        if (!shippingInfo) {
          if (addrs.length > 0) {
            const def = addrs.find((a) => a.is_default) || addrs[0];
            setShippingInfo({
              fullName: def.full_name,
              address: def.address,
              city: def.city || '',
              phone: def.phone,
              provinceId: def.province_id || '',
              districtId: def.district_id || '',
              provinceName: def.province?.name || '',
              districtName: def.district?.name || '',
              selectedAddressId: def.id,
              saveToAddressBook: false,
            });
          } else if (user) {
            // No saved address — pre-fill whatever the user has on their profile.
            // Province/district aren't on the User profile, so the form will still
            // require those before continuing.
            setShippingInfo({
              fullName: user.full_name || '',
              address: user.address || '',
              city: user.city || '',
              phone: user.phone_number || '',
              provinceId: '',
              districtId: '',
              provinceName: '',
              districtName: '',
              selectedAddressId: null,
              saveToAddressBook: true,
            });
          }
        }
        setDefaultAddressLoaded(true);
      })
      .catch(() => setDefaultAddressLoaded(true));
  }, [isAuthenticated, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Recompute the quote whenever district / method / payment / subtotal changes
  useEffect(() => {
    const districtId = shippingInfo?.districtId;
    if (!districtId) {
      setQuote(null);
      return;
    }
    let cancelled = false;
    shippingService
      .getQuote({
        districtId,
        method: shippingMethod,
        subtotal,
        paymentMethod,
      })
      .then((q) => {
        if (!cancelled) setQuote(q);
      })
      .catch((err) => {
        if (!cancelled) {
          setQuote(null);
          setError(err?.response?.data?.message || 'Could not calculate shipping');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [shippingInfo?.districtId, shippingMethod, paymentMethod, subtotal]);

  const handleShippingSubmit = async (data) => {
    try {
      setError(null);
      const next = {
        fullName: data.fullName,
        address: data.address,
        city: data.city || '',
        phone: data.phone,
        provinceId: data.provinceId,
        districtId: data.districtId,
        provinceName: data.provinceName,
        districtName: data.districtName,
        selectedAddressId: data.selectedAddressId,
        saveToAddressBook: data.saveToAddressBook,
      };
      setShippingInfo(next);

      // Persist to address book if requested and this is a new address
      if (data.saveToAddressBook && !data.selectedAddressId) {
        try {
          const created = await addressService.create({
            full_name: data.fullName,
            phone: data.phone,
            address: data.address,
            city: data.city || null,
            province_id: data.provinceId,
            district_id: data.districtId,
          });
          if (created) {
            setSavedAddresses((prev) => [created, ...prev]);
            next.selectedAddressId = created.id;
          }
        } catch {
          // Don't block checkout if save fails
        }
      }

      setStep(2);
    } catch (err) {
      setError(err.message || 'Failed to save shipping info');
    }
  };

  const handleShippingMethodSelect = (method) => {
    setShippingMethod(method);
  };

  const handlePaymentMethodSelect = (method) => {
    setPaymentMethod(method);
  };

  const handlePlaceOrder = async () => {
    try {
      setLoading(true);
      setError(null);

      const orderData = {
        shipping_full_name: shippingInfo.fullName,
        shipping_address: shippingInfo.address,
        shipping_city: shippingInfo.city || '',
        shipping_district_id: shippingInfo.districtId,
        shipping_phone: shippingInfo.phone,
        shipping_method: shippingMethod,
        payment_method: paymentMethod,
      };

      const result = await orderService.createOrder(orderData);

      if (paymentMethod === PAYMENT_METHODS.KHALTI) {
        const orderId = result?.order?.id || result?.id;
        if (!orderId) throw new Error('Order created but ID not returned');
        const khaltiData = await orderService.initiateKhaltiPayment(orderId);
        if (khaltiData?.payment_url) {
          window.location.href = khaltiData.payment_url;
          return;
        }
        throw new Error('Failed to get Khalti payment URL');
      }

      if (paymentMethod === PAYMENT_METHODS.ESEWA) {
        const orderId = result?.order?.id || result?.id;
        if (!orderId) throw new Error('Order created but ID not returned');
        const esewaData = await orderService.initiateEsewaPayment(orderId);
        if (esewaData?.gateway_url && esewaData?.params) {
          // eSewa only accepts an HTML form POST — render and submit one.
          const form = document.createElement('form');
          form.method = 'POST';
          form.action = esewaData.gateway_url;
          Object.entries(esewaData.params).forEach(([name, value]) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = value;
            form.appendChild(input);
          });
          document.body.appendChild(form);
          form.submit();
          return;
        }
        throw new Error('Failed to get eSewa payment payload');
      }

      navigate('/profile?orderSuccess=true');
    } catch (err) {
      setError(err.message || 'Failed to place order');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated || !cart || cartItemCount === 0 || !defaultAddressLoaded) {
    return <Loading />;
  }

  return (
    <div className="checkout-page">
      <div className="checkout-header">
        <h1>Checkout</h1>
        <div className="checkout-steps">
          <div className={`step ${step >= 1 ? 'active' : ''}`}>1. Shipping</div>
          <div className={`step ${step >= 2 ? 'active' : ''}`}>2. Shipping Method</div>
          <div className={`step ${step >= 3 ? 'active' : ''}`}>3. Payment</div>
          <div className={`step ${step >= 4 ? 'active' : ''}`}>4. Review</div>
        </div>
      </div>

      <div className="checkout-content">
        <div className="checkout-form">
          {step === 1 && (
            <ShippingForm
              onSubmit={handleShippingSubmit}
              defaultValues={shippingInfo || {}}
              savedAddresses={savedAddresses}
            />
          )}

          {step === 2 && (
            <div>
              <ShippingMethod
                selectedMethod={shippingMethod}
                onSelect={handleShippingMethodSelect}
                districtId={shippingInfo?.districtId}
                subtotal={subtotal}
                paymentMethod={paymentMethod}
              />
              <div className="checkout-actions">
                <button className="btn-secondary" onClick={() => setStep(1)}>
                  Back
                </button>
                <button
                  className="btn-primary"
                  onClick={() => setStep(3)}
                  disabled={!shippingMethod || !quote}
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <PaymentMethod
                selectedMethod={paymentMethod}
                onSelect={handlePaymentMethodSelect}
                codSurcharge={quote?.cod_surcharge || 0}
              />
              <div className="checkout-actions">
                <button className="btn-secondary" onClick={() => setStep(2)}>
                  Back
                </button>
                <button
                  className="btn-primary"
                  onClick={() => setStep(4)}
                  disabled={!paymentMethod}
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div>
              <OrderSummary shippingInfo={shippingInfo} quote={quote} />
              {error && <div className="error-message">{error}</div>}
              <div className="checkout-actions">
                <button className="btn-secondary" onClick={() => setStep(3)}>Back</button>
                <button
                  className="btn-primary"
                  onClick={handlePlaceOrder}
                  disabled={loading || !quote}
                >
                  {loading ? 'Placing Order...' : 'Place Order'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Checkout;
